import { useAuth0 } from '@auth0/auth0-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  NavigationContextButton,
  Spotlight,
} from '@ths/ui';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { CompanyMenuItems } from './CompanyMenuItems';
import { useHotelScope } from './scope';
import { useOptionalHotelSession } from './session';

// Account-wide across clients. Keep dismissal for this browser even when storage is unavailable.
const dismissedGuides = new Set<string>();
const guideKey = (actorId: string) => `ths:client-switcher-guide:v1:${actorId}`;

function ClientSwitcherGuide({
  actorId,
  children,
}: {
  actorId: string;
  children: ReactNode;
}) {
  const storageKey = guideKey(actorId);
  const [open, setOpen] = useState(() => {
    if (dismissedGuides.has(storageKey)) return false;
    try {
      return localStorage.getItem(storageKey) !== 'dismissed';
    } catch {
      return true;
    }
  });
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === storageKey && event.newValue === 'dismissed')
        setOpen(false);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [storageKey]);

  return (
    <Spotlight
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) {
          dismissedGuides.add(storageKey);
          try {
            localStorage.setItem(storageKey, 'dismissed');
          } catch {
            // This session still remembers dismissal when browser storage is blocked.
          }
        }
      }}
      title="Switch companies here."
    >
      {children}
    </Spotlight>
  );
}

export function ClientSwitcher() {
  const { user, isLoading } = useAuth0();
  const scope = useHotelScope();
  const session = useOptionalHotelSession();
  const [selecting, setSelecting] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const selectedClientId = useRef<string | null>(null);
  if (!scope || !session || !scope.hasMultipleClients) return null;

  const control = (
    <DropdownMenuTrigger asChild>
      <NavigationContextButton
        ref={trigger}
        value={scope.clientName}
        aria-label={`Switch company, current company: ${scope.clientName}`}
      />
    </DropdownMenuTrigger>
  );

  return (
    <DropdownMenu open={selecting} onOpenChange={setSelecting} modal={false}>
      {user?.sub && !isLoading ? (
        <ClientSwitcherGuide key={user.sub} actorId={user.sub}>
          {control}
        </ClientSwitcherGuide>
      ) : (
        control
      )}
      <DropdownMenuContent
        align="end"
        collisionPadding={16}
        className="max-h-[min(20rem,var(--radix-dropdown-menu-content-available-height))] w-[var(--radix-dropdown-menu-trigger-width)] max-w-[calc(100vw-2rem)] overflow-y-auto border border-separator"
        onCloseAutoFocus={(event) => {
          const clientId = selectedClientId.current;
          selectedClientId.current = null;
          if (!clientId) return;
          // Close the menu and restore its trigger before opening a draft warning.
          event.preventDefault();
          trigger.current?.focus();
          session.requestCompanySwitch(clientId);
        }}
      >
        {selecting && (
          <CompanyMenuItems
            currentClientId={scope.clientId}
            onSelect={(clientId) => {
              selectedClientId.current = clientId;
            }}
          />
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
